import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, SkipSelf } from '@angular/core';
import { QuestionStatusService } from '@app/pages/questions/question-status.service';
import {
	IQuestionnaireGroup,
	IQuestionnaireItem,
	IQuestionnaireOverviewItem,
} from '@interfaces/interfaces';
import { BlockQuestionItem, SpecialQuestionItem } from '@interfaces/questions';
import {
	ISpecialQuestion,
	SpecialQuestionService,
} from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { WorkflowItemDataDirective } from '@workflow/directives/workflow-item-data/workflow-item-data.directive';
import { SessionItemData } from '@workflow/services/session-item-data';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { map, skip, switchMap, takeUntil } from 'rxjs/operators';
import { isNil } from 'lodash';

@Component({
	selector: 'workflow-alternate-input-modal',
	templateUrl: './alternate-input-modal.component.html',
	styleUrls: ['./alternate-input-modal.component.scss'],
})
export class AlternateInputModalComponent implements OnChanges, OnInit, OnDestroy {

	@Input() finishedAndLocked: boolean;
	@Input() isAlternateInputModalVisible: boolean;
	@Input() softLocked: boolean;
	@Input() validationLevel: 'error' | 'warning';
	@Input() question: IQuestionnaireItem;
	@Input() hasWorkflowWritePermission = false;

	_parentOverviewConfigs: IQuestionnaireOverviewItem[];
	@Input() set parentOverviewConfigs(config: IQuestionnaireOverviewItem[]) {
		this._parentOverviewConfigs = config || null;
		this.updateGroupOverview();
	}

	@Output() isAlternateInputModalNotVisible = new EventEmitter<boolean>();
	@Output() valid = new EventEmitter<boolean>();

	public defaultGroupConfiguration: IQuestionnaireGroup;
	public modalData: SessionItemData = new SessionItemData();
	public allValid = false;
	private validStatus: boolean[] = [];
	public okText = 'actions.accept';

	public itemsEnabled: boolean[] = [];
	public waitForChange: boolean[] = [];

	private subscription = new Subscription();
	private update$ = new EventEmitter<never>();
	private hidden$ = new EventEmitter<never>();

	constructor(
		@SkipSelf() private workflowItemDataDirective: WorkflowItemDataDirective,
		private questionStatusService: QuestionStatusService,
		private specialQuestionService: SpecialQuestionService,
	) {}

	ngOnInit(): void {
		const pipe = this.update$.pipe(
			switchMap(() => this.getItemsEnabled$()),
		);
		this.subscription.add(pipe.subscribe(getItemsEnabled => {
			this.itemsEnabled = (getItemsEnabled || [])?.map((value, index) => {
				if (value !== this.itemsEnabled[index] && this.waitForChange[index]) {
					return this.itemsEnabled[index];
				}
				else {
					return getItemsEnabled[index];
				}
			});
		}));
		this.update$.emit();
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	ngOnChanges(changes: SimpleChanges): void {

		if (changes.question) {
			if (this.question && this.specialQuestionService.isSpecialQuestion(this.question.type)) {
				const specialQuestion = this.question as SpecialQuestionItem;
				const specialQuestionImpl = this.specialQuestionService.getSpecialQuestionByType(specialQuestion.type);
				this.setDefaultGroupConfiguration(specialQuestionImpl, specialQuestion);
				this.update$.emit();
			} else {
				this.defaultGroupConfiguration = undefined;
			}
			this.validStatus = [];
			this.update$.emit();
		}

		if (changes.finishedAndLocked) {
			this.okText = this.finishedAndLocked ? 'actions.close' : 'actions.accept';
		}

		//Reset Data
		if (changes.isAlternateInputModalVisible && this.isAlternateInputModalVisible) {
			this.subscription.add(this.getSessionData$().pipe(
				switchMap(sessionItemData => {
					this.modalData = new SessionItemData(sessionItemData.data);
					this.update$.emit();
					return this.modalData.data$.pipe(skip(1));
				}),
				takeUntil(this.hidden$),
			).subscribe(() => this.save()));
		}
		if (changes.isAlternateInputModalVisible && !this.isAlternateInputModalVisible) {
			this.hidden$.emit();
		}
	}

	private getItemsEnabled$(): Observable<boolean[]> {
		const hasShowIf = this.defaultGroupConfiguration?.items?.some(item => !!item.showIf);
		if (!hasShowIf) {
			const isEnabled = this.defaultGroupConfiguration?.items.map(() => true);
			return of(isEnabled);
		}

		const items = this.defaultGroupConfiguration.items;
		const data$ = this.getSessionData$();
		this.waitForChange = items.map(() => false);
		const observables = items.map((item, index) => {
			if (item.showIf) {
				return data$.pipe(
					switchMap(data => data?.property$(item.showIf)),
					map(value => {
						if (isNil(value)) {
							this.waitForChange[index] = true;
						}
						return !!value;
					}),
				);
			} else {
				return of(true);
			}
		});
		return combineLatest(observables);
	}

	closeAlternateInputModal(): void {
		this.closeModal();
	}

	/**
	 * recreate a result object containing only the properties from the alternate input block question
	 *
	 * @returns new result object
	 */
	private buildQuestionData(): unknown {
		const specialQuestion = this.question as SpecialQuestionItem;
		const tmp = new SessionItemData();
		for (const [index, question] of this.defaultGroupConfiguration.items.entries()) {
			if (question.type === 'BlockQuestion' && this.itemsEnabled[index]) {
				const blockQuestion = question as BlockQuestionItem;
				for (const block of blockQuestion.items) {
					const key = block.key;
					const value = this.modalData.getProperty(key);
					tmp.setProperty(key, value);
				}
			}
		}
		return tmp.getProperty(specialQuestion.key);
	}

	private save(): void {
		if (!this.finishedAndLocked) {
			const specialQuestion = this.question as SpecialQuestionItem;
			const result = this.buildQuestionData();
			this.getSessionData().setProperty(specialQuestion.key, result);
		}
	}

	saveAlternateInputs(): void {
		this.save();
		this.closeModal();
	}

	setDefaultGroupConfiguration(specialQuestion: ISpecialQuestion, specialQuestionItem: SpecialQuestionItem): void {
		this.defaultGroupConfiguration = cloneDeep(specialQuestion.defaultGroupConfiguration);
		const parentKey = specialQuestionItem.baseKey || specialQuestionItem.key;
		for (const item of this.defaultGroupConfiguration.items) {
			if (item.type === 'BlockQuestion') {
				const bqItem = item as BlockQuestionItem;
				if (bqItem.missingValueKey) {
					bqItem.missingValueKey = parentKey + '.' + bqItem.missingValueKey;
				}

				for (const questionItem of bqItem.items) {
					questionItem.key = specialQuestionItem.key + '.' + questionItem.key;
				}
			}
		}
		if (specialQuestion.missingValueKeyGroup) {
			this.defaultGroupConfiguration.missingValueKey = parentKey + '.' + specialQuestion.missingValueKeyGroup;
		}
		this.updateGroupOverview();
	}

	private updateGroupOverview(): void {
		if (!this.defaultGroupConfiguration) {
			return;
		}

		this.defaultGroupConfiguration.groupOverview ||= this._parentOverviewConfigs || null;
	}

	private closeModal(): void {
		this.isAlternateInputModalVisible = false;
		this.isAlternateInputModalNotVisible.emit(false);
	}

	setValid(valid: boolean, index: number): void {
		this.validStatus[index] = valid;
		if (this.finishedAndLocked) {
			this.allValid = true;
		} else {
			const status = this.questionStatusService.checkQuestionGroupStatus(this.defaultGroupConfiguration, this.modalData);
			this.allValid = status.isSufficient && this.validStatus.filter(vStatus => vStatus === false).length < 1;
		}
		this.valid.emit(this.allValid);
		this.update$.emit();
	}

	getSessionData(): SessionItemData {
		return this.finishedAndLocked ? this.workflowItemDataDirective.itemFull : this.workflowItemDataDirective.item;
	}

	getSessionData$(): Observable<SessionItemData> {
		return this.finishedAndLocked ? this.workflowItemDataDirective.itemFull$ : this.workflowItemDataDirective.data$;
	}

}
